import { Spacing } from '@persona-id/design-tokens';
import { LinkExternalIcon } from '@persona-id/design-tokens/icons';
import { FC, useMemo } from 'react';
import styled from 'styled-components';

import Accordion from 'dashboard/components/Accordion';
import WorkflowStatusSticker from 'dashboard/components/WorkflowStatusSticker';
import {
  ConnectionNode,
  InquiryTemplateNode,
  PlatformConfigType,
  WorkflowNode,
} from 'dashboard/models/platform-node';
import { useGetPlatformConnectionsQuery } from 'dashboard/redux/api/platform-node';
import { NODE_TYPE_TO_LABEL } from 'dashboard/views/DashboardPlatformConnectionsShowView/constants';
import { routeToNode } from 'dashboard/views/DashboardPlatformConnectionsShowView/PlatformNodeTypes/PlatformNodeLink';
import Spacer from 'design-system/components/Spacer';
import { Label, Paragraph } from 'design-system/components/Text';
import useRTKErrorHandler from 'lib/hooks/useRTKErrorHandler';
import { ApiModel } from 'lib/interfaces/api';

const ContentContainer = styled('div')`
  display: flex;
  align-items: stretch;
  flex-direction: column;
  justify-content: space-between;
  gap: ${Spacing[2]};
`;

const CardContainer = styled('div')`
  background: ${({ theme }) => theme.color.variants.caution.disabled};
  padding: ${Spacing[2]};
  border-radius: ${({ theme }) => theme.element.borderRadius};
  width: 100%;
`;

const ReferencedWorkflowItem = styled('div')`
  padding: ${Spacing[3]};
  display: grid;
  grid-template-columns: 30% 50% 10% 10%;
  background: ${({ theme }) => theme.color.primary.white};
  border-bottom: 1px solid ${({ theme }) => theme.color.border.primary};
`;

const StyledLink = styled('a')`
  display: flex;
`;

const isWorkflowNode = (node: ConnectionNode): node is WorkflowNode => {
  return node.type === PlatformConfigType.Workflow;
};

interface PlatformConnectionsModalContentProps {
  platformNodeId: string;
}

const PlatformConnectionsModalContent: FC<PlatformConnectionsModalContentProps> = ({
  platformNodeId,
}) => {
  const platformConnectionQuery = useGetPlatformConnectionsQuery(platformNodeId, {
    refetchOnMountOrArgChange: true,
  });

  const { data: platformConnectionQueryResponse, error: platformConnectionQueryError } =
    platformConnectionQuery;

  useRTKErrorHandler({
    error: platformConnectionQueryError,
    onError: () => ({ message: 'Failed to load platform connections' }),
  });

  const [dependencyNodes, downstreamWorkflows]: [
    dependencyNodes: (InquiryTemplateNode | WorkflowNode)[],
    downstreamWorkflows: WorkflowNode[],
  ] = useMemo(() => {
    if (!platformConnectionQueryResponse?.rootNode) {
      return [[], []];
    }

    return [
      platformConnectionQueryResponse.inboundConnections.filter(
        (node): node is WorkflowNode | InquiryTemplateNode =>
          (isWorkflowNode(node) && node.metadata['trigger-type'] !== ApiModel.TriggerDirect) ||
          node.type === PlatformConfigType.InquiryTemplate,
      ),
      platformConnectionQueryResponse.outboundConnections.filter(
        (node): node is WorkflowNode =>
          isWorkflowNode(node) &&
          node.depth === 1 &&
          node.metadata['trigger-type'] !== ApiModel.TriggerCase,
      ),
    ];
  }, [platformConnectionQueryResponse]);

  const platformNodeType = platformConnectionQueryResponse?.rootNode
    ? NODE_TYPE_TO_LABEL[platformConnectionQueryResponse.rootNode.type]
    : 'configuration';

  return (
    <ContentContainer>
      {dependencyNodes.length > 0 && (
        <>
          <Paragraph>
            This {platformNodeType} is used in the following configurations. Deactivating it will
            cause errors in these active configurations.
          </Paragraph>
          <Spacer height={1} />
          <CardContainer>
            <Accordion
              title={
                <Label weight='bold' color='primary'>
                  Platform Dependencies
                </Label>
              }
              titleGapSize={Spacing[1]}
            >
              <Spacer height={1} />
              <PlatformDependenciesTableContent dependencyNodes={dependencyNodes} />
            </Accordion>
          </CardContainer>
        </>
      )}
      <Spacer height={1} />
      {downstreamWorkflows.length > 0 && (
        <>
          <Paragraph>
            This {platformNodeType} triggers the following workflows. Deactivating it will halt all
            downstream events.
          </Paragraph>
          <Spacer height={1} />
          <CardContainer>
            <Accordion
              title={
                <Label weight='bold' color='primary'>
                  Workflow References
                </Label>
              }
              titleGapSize={Spacing[1]}
            >
              <Spacer height={1} />
              <PlatformDependenciesTableContent dependencyNodes={downstreamWorkflows} />
            </Accordion>
          </CardContainer>
        </>
      )}
    </ContentContainer>
  );
};
interface WorkflowsTableContentProps {
  dependencyNodes: (WorkflowNode | InquiryTemplateNode)[];
}

const PlatformDependenciesTableContent: FC<WorkflowsTableContentProps> = ({ dependencyNodes }) => {
  return (
    <>
      {dependencyNodes.map((node) => (
        <ReferencedWorkflowItem key={node.token}>
          <Label>{node.metadata.name}</Label>
          <Label color='secondary'>{node.token}</Label>
          <Label>
            {isWorkflowNode(node) && (
              <WorkflowStatusSticker workflowStatus={node.metadata.status} />
            )}
          </Label>
          <StyledLink
            aria-label={`${node.metadata.name} link`}
            href={routeToNode(node)}
            target='_blank'
            rel='noreferrer'
          >
            Edit
            <LinkExternalIcon />
          </StyledLink>
        </ReferencedWorkflowItem>
      ))}
    </>
  );
};

export default PlatformConnectionsModalContent;
